import { clone } from "ramda";
import { useEffect, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  ActionButton,
  ActionButtonIconPlacement,
  ActionButtonIntent,
  Icons,
  Item,
  MenuListBox,
  MenuOptionItem,
  Popover,
  Section,
  toTimeAgo,
  useContextualMenu,
} from "widgets";

import type { Project, User } from "@/graphql.ts";
import { ProjectColoredBadge } from "@/modules/projects/components/ProjectColoredWidgets.tsx";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { showNavbarElement } from "@/redux/modules/navbar.ts";
import {
  getCreateNewProjectButtonProps,
  type IProjectListState,
  loadProjectList,
} from "@/redux/modules/project-list.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";

export function ProjectsMenu() {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  const { projects } = useSelector<IAppState, IProjectListState>((state) => state.projectList, shallowEqual);
  const userState = useSelector<IAppState, User>((state) => state.auth.session, shallowEqual);

  const newProjectButtonProps = getCreateNewProjectButtonProps(userState, () =>
    dispatch(showNavbarElement({ showCreateProjectDialog: true })),
  );

  // The first is always the private workspace
  // We need to clone it to avoid to mutate redux
  const clonedProjects = clone(projects);
  let privateProject: Project | null = null;
  // clonedProjects = clonedProjects.sort((p1, p2) => (p1.name ?? "").localeCompare(p2.name ?? "", "en", { sensitivity: "base" }));
  if (clonedProjects[0]?.isPrivateWorkspace) {
    privateProject = clonedProjects.shift();
  }

  const triggerRef = useRef(null);
  const { state, triggerProps, menuProps } = useContextualMenu(triggerRef);

  useEffect(() => {
    // reload the project list every time the menu is open
    // the loading should be discrete (no big loading glitches) as one assumes
    // the projects list doesn't change that often.
    dispatch(loadProjectList());
  }, [state.isOpen]);

  return (
    <>
      <ActionButton
        {...triggerProps}
        ref={triggerRef}
        label={getProjectLabel(true, true)}
        intent={state.isOpen ? ActionButtonIntent.Active : ActionButtonIntent.Secondary}
        icon={"keyboard_arrow_down"}
        iconPlacement={ActionButtonIconPlacement.Right}
      />
      <Popover state={state} triggerRef={triggerRef} placement={"bottom start"}>
        <MenuListBox
          {...menuProps}
          onAction={(key) => {
            switch (key) {
              case "__all_projects__":
                navigate("/projects");
                break;
              case "__new_project__":
                newProjectButtonProps.onPress();
                break;
              default:
                navigate(`/project/${key}`);
                break;
            }
          }}
          disabledKeys={newProjectButtonProps.disabled ? ["__new_project__"] : undefined}
          style={{ width: 380 }}
        >
          <Section>
            <Item key={"__all_projects__"}>
              <MenuOptionItem
                title={`View All ${getProjectLabel(true, true)}`}
                icon={
                  <img
                    src={Icons.Action.AllProjects}
                    width={20}
                    height={20}
                    alt={`View All ${getProjectLabel(true, true)}`}
                  />
                }
              />
            </Item>
            <Item key={"__new_project__"}>
              <MenuOptionItem
                title={newProjectButtonProps.children}
                icon={
                  <img src={Icons.Action.NewProjects} width={20} height={20} alt={newProjectButtonProps.children} />
                }
              />
            </Item>
          </Section>
          {privateProject && (
            <Section title={"My Places"}>
              <Item key={privateProject.id}>
                <MenuOptionItem
                  onAction={() => {
                    navigate(`/project/${privateProject.id}`);
                  }}
                  id={privateProject.id}
                  icon={<ProjectColoredBadge project={privateProject} noLink size={"menu"} />}
                  tag={privateProject.lastModified ? toTimeAgo(privateProject.lastModified) : undefined}
                  title={privateProject.name}
                  description={privateProject.description}
                />
              </Item>
            </Section>
          )}
          <Section title={getProjectLabel(true, true)}>
            {clonedProjects.map((project) => (
              <Item key={project.id}>
                <MenuOptionItem
                  id={project.id}
                  icon={<ProjectColoredBadge project={project} noLink size={"menu"} />}
                  tag={project.lastModified ? toTimeAgo(project.lastModified) : undefined}
                  title={project.name}
                  description={project.description}
                />
              </Item>
            ))}
          </Section>
        </MenuListBox>
      </Popover>
    </>
  );
}
